import type React from "react";
import { useCallback } from "react";
import type { BarSpan } from "./DensityChart.lib";
import type { PanDirection } from "./densityViewport.lib";
import { useEdgePan } from "./useEdgePan";
import type { DragOrigin } from "./windowDragOrigin";
import { isEdgeHeld } from "./windowSpan.lib";

interface UseWindowEdgePanParams {
    /** The visible area whose edges start the pan. */
    edgeRef: React.RefObject<HTMLElement | null>;
    onEdgePan: (px: number) => void;
    originRef: React.RefObject<DragOrigin | null>;
    draftRef: React.RefObject<BarSpan | null>;
}

/**
 * `useEdgePan` for a dragged window: it stops panning a way once the edge
 * being dragged is held against the other one and can't follow.
 */
export function useWindowEdgePan({
    edgeRef,
    onEdgePan,
    originRef,
    draftRef,
}: UseWindowEdgePanParams) {
    const canPanToward = useCallback(
        (direction: PanDirection) => {
            const origin = originRef.current;
            const draft = draftRef.current;
            return (
                !origin ||
                !draft ||
                !isEdgeHeld({
                    span: draft,
                    from: origin.span,
                    mode: origin.mode,
                    direction,
                })
            );
        },
        [draftRef, originRef],
    );
    return useEdgePan({ edgeRef, onEdgePan, canPanToward });
}
